import { useState } from 'react';
import { useTranslation } from '@mukil/i18n';
import {
  Button,
  EmptyState,
  GlassTable,
  SkeletonList,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@jumpstone/ui-react';
import { ScreenHeader } from '../../components/common/screen_header';
import { LEAVE_STATUSES, resolve_leave_view } from './leave_utils';
import { LEAVE_STATUS_KEY } from '../../lib/label_keys';
import {
  filter_by_status,
  useLeaveRequests,
} from '../../data/use_leave_requests';
import type {
  LeaveActionFailure,
  LeaveCancelFailure,
} from '../../data/use_leave_requests';
import type { LeaveRequest } from '../../data/wagepro_store_leave';
import { navigate } from '../../lib/router';
import type { LeaveTab } from '../../lib/router';
import { LeaveRejectDialog } from './leave_reject_dialog';
import { LeaveApproveConfirmDialog } from './leave_approve_confirm_dialog';
import { LeaveTableRow } from './leave_table_row';
import { LeaveRecordDrawer } from './leave_record_drawer';
import { LeaveBalancesTab } from './leave_balances_tab';
import { resolveEmployeesStatus } from './leave_opening_balance_utils';

/** One row's most recent approve/reject failure, rendered inline beneath its own action buttons — never a raw error toast. */
interface RowActionError {
  request_id: string;
  failure: LeaveActionFailure;
}

/** One row's most recent cancel failure. */
interface RowCancelError {
  request_id: string;
  failure: LeaveCancelFailure;
}

/**
 * Leave-request queues with approve/reject/cancel-behind-confirmation actions
 * and a balances tab. Also hosts the "Record leave" drawer for employer-on-behalf
 * submissions (#wagepro-leave-phase2, this implementation).
 */
export function LeaveScreen({ tab }: { tab: LeaveTab }): React.JSX.Element {
  const { t, locale } = useTranslation('wp');
  const {
    state,
    counts,
    approve,
    reject,
    cancel,
    request,
    retry,
    employees,
    mutation_version,
  } = useLeaveRequests();
  const [approving_id, set_approving_id] = useState<string | null>(null);
  const [row_error, set_row_error] = useState<RowActionError | null>(null);
  const [cancel_error, set_cancel_error] = useState<RowCancelError | null>(
    null,
  );
  const [reject_target, set_reject_target] = useState<LeaveRequest | null>(
    null,
  );
  const [approve_target, set_approve_target] = useState<LeaveRequest | null>(
    null,
  );
  const [record_open, set_record_open] = useState(false);

  const requests = state.status === 'ready' ? state.requests : [];
  const rows = tab === 'balances' ? [] : filter_by_status(requests, tab);
  const view =
    tab === 'balances'
      ? ('ready' as const)
      : resolve_leave_view({
          state,
          filtered_row_count: rows.length,
        });
  const can_act = approve !== undefined && reject !== undefined;

  return (
    <>
      <ScreenHeader
        title={t('leave.title')}
        description={t('leave.subtitle')}
        actions={
          request !== undefined ? (
            <Button
              variant="primary"
              size="sm"
              onClick={() => set_record_open(true)}
            >
              {t('leave.record_action')}
            </Button>
          ) : undefined
        }
      />

      <Tabs
        value={tab}
        onValueChange={(next) => {
          set_row_error(null);
          set_cancel_error(null);
          navigate({ module: 'leave', tab: next as LeaveTab });
        }}
      >
        <TabsList className="wp-tabstrip mb-3.5">
          {LEAVE_STATUSES.map((status) => (
            <TabsTrigger key={status} value={status}>
              {t(LEAVE_STATUS_KEY[status])}{' '}
              <span className="ms-1 text-[11px] text-[color:var(--ink-muted)] num">
                {counts[status]}
              </span>
            </TabsTrigger>
          ))}
          <TabsTrigger value="balances">{t('leave.tab_balances')}</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* ── Balances tab ───────────────────────────────────────────────── */}
      {tab === 'balances' && (
        <LeaveBalancesTab
          employees={employees}
          employees_status={resolveEmployeesStatus(state.status)}
          refresh_key={mutation_version}
        />
      )}

      {/* ── Request queue tabs ─────────────────────────────────────────── */}
      {tab !== 'balances' && (
        <>
          {view === 'loading' && (
            <GlassTable>
              <div className="p-4">
                <span className="sr-only">{t('leave.loading')}</span>
                <SkeletonList rows={5} />
              </div>
            </GlassTable>
          )}

          {view === 'no_entity' && (
            <EmptyState
              title={t('leave.no_entity_title')}
              description={t('leave.no_entity_description')}
            />
          )}

          {view === 'entitlement_required' && (
            <EmptyState
              title={t('leave.entitlement_required_title')}
              description={t('leave.entitlement_required_description')}
            />
          )}

          {view === 'error' && (
            <EmptyState
              title={t('leave.load_error_title')}
              description={t('leave.load_error_description')}
              action={
                <Button variant="secondary" onClick={retry}>
                  {t('leave.retry')}
                </Button>
              }
            />
          )}

          {view === 'empty' && (
            <EmptyState
              title={t('leave.empty_title')}
              description={t('leave.empty_description')}
            />
          )}

          {view === 'rows' && (
            <GlassTable>
              <table className="nv-table">
                <thead>
                  <tr>
                    <th scope="col">{t('leave.column_employee')}</th>
                    <th scope="col">{t('leave.column_type')}</th>
                    <th scope="col">{t('leave.column_dates')}</th>
                    <th scope="col" className="text-end">
                      {t('leave.column_days')}
                    </th>
                    <th scope="col">{t('leave.column_status')}</th>
                    <th scope="col">
                      <span className="sr-only">{t('common.actions')}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((leaveRequest) => (
                    <LeaveTableRow
                      key={leaveRequest.id}
                      request={leaveRequest}
                      locale={locale}
                      can_act={can_act}
                      is_approving={approving_id === leaveRequest.id}
                      action_failure={
                        row_error && row_error.request_id === leaveRequest.id
                          ? row_error.failure
                          : undefined
                      }
                      cancel_failure={
                        cancel_error &&
                        cancel_error.request_id === leaveRequest.id
                          ? cancel_error.failure
                          : undefined
                      }
                      on_approve_click={() => {
                        set_row_error(null);
                        set_cancel_error(null);
                        set_approve_target(leaveRequest);
                      }}
                      on_reject_click={() => {
                        set_row_error(null);
                        set_cancel_error(null);
                        set_reject_target(leaveRequest);
                      }}
                      on_cancel={
                        cancel !== undefined
                          ? async () => {
                              set_cancel_error(null);
                              const result = await cancel(leaveRequest.id);
                              if (result.outcome === 'failure') {
                                set_cancel_error({
                                  request_id: leaveRequest.id,
                                  failure: result.failure,
                                });
                              }
                            }
                          : undefined
                      }
                    />
                  ))}
                </tbody>
              </table>
            </GlassTable>
          )}

          <LeaveApproveConfirmDialog
            request={approve_target}
            onOpenChange={(open) => {
              if (!open) {
                set_approve_target(null);
                set_approving_id(null);
              }
            }}
            approve={approve}
            onFailure={(request_id, failure) => {
              set_row_error({ request_id, failure });
              set_approve_target(null);
              set_approving_id(null);
            }}
          />

          <LeaveRejectDialog
            request={reject_target}
            onOpenChange={(open) => {
              if (!open) set_reject_target(null);
            }}
            reject={reject}
            onFailure={(request_id, failure) => {
              set_row_error({ request_id, failure });
              set_reject_target(null);
            }}
          />
        </>
      )}

      {request !== undefined && (
        <LeaveRecordDrawer
          open={record_open}
          onOpenChange={set_record_open}
          employees={employees}
          onSuccess={() => {
            set_record_open(false);
          }}
          request={request}
        />
      )}
    </>
  );
}
